import { Container } from '@/components/ui/Container';
import { media } from '@/styles/breakpoints';
import { motion } from 'framer-motion';
import styled from 'styled-components';

/* ── Hero ─────────────────────────────────────────────────── */

export const HeroSection = styled.section`
  position: relative;
  padding-top: calc(
    ${({ theme }) => theme.layout.headerHeight} + ${({ theme }) => theme.spacing[16]}
  );
  padding-bottom: ${({ theme }) => theme.spacing[20]};
  overflow: hidden;
  background: linear-gradient(
    160deg,
    ${({ theme }) => theme.colors.navy[950]} 0%,
    ${({ theme }) => theme.colors.navy[800]} 60%,
    ${({ theme }) => theme.colors.steel[500]} 100%
  );
`;

export const HeroGrid = styled.div`
  position: absolute;
  inset: 0;
  background-image: radial-gradient(
    circle at 1px 1px,
    rgba(255, 255, 255, 0.06) 1px,
    transparent 0
  );
  background-size: 40px 40px;
  pointer-events: none;
`;

export const HeroGlow = styled.div`
  position: absolute;
  top: -100px;
  right: -100px;
  width: 600px;
  height: 600px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0, 168, 240, 0.15) 0%, transparent 70%);
  pointer-events: none;
`;

export const HeroContent = styled(Container)`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
  max-width: 800px;
`;

/* ── Linha do tempo ────────────────────────────────────────── */

export const TimelineWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[0]};
  margin-top: ${({ theme }) => theme.spacing[12]};

  /* Linha vertical */
  &::before {
    content: '';
    position: absolute;
    left: 16px;
    top: 8px;
    bottom: 8px;
    width: 2px;
    background: linear-gradient(
      to bottom,
      ${({ theme }) => theme.colors.cyan[400]},
      ${({ theme }) => theme.colors.steel[500]},
      transparent
    );

    ${media.tablet} {
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

export const TimelineItem = styled(motion.div)<{ $side: 'left' | 'right' }>`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[6]};
  padding-left: ${({ theme }) => theme.spacing[10]};
  padding-bottom: ${({ theme }) => theme.spacing[10]};

  ${media.tablet} {
    width: 50%;
    padding-left: 0;
    padding-bottom: ${({ theme }) => theme.spacing[12]};
    align-self: ${({ $side }) => ($side === 'left' ? 'flex-start' : 'flex-end')};
    ${({ $side, theme }) =>
      $side === 'left'
        ? `padding-right: ${theme.spacing[10]};`
        : `padding-left: ${theme.spacing[10]};`}
  }
`;

export const TimelineDot = styled.div`
  position: absolute;
  left: 8px;
  top: 6px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.cyan[400]};
  border: 3px solid ${({ theme }) => theme.colors.navy[900]};
  flex-shrink: 0;
  z-index: 1;

  ${media.tablet} {
    left: auto;
    right: -9px;
  }
`;

export const TimelineDotRight = styled(TimelineDot)`
  ${media.tablet} {
    left: -9px;
    right: auto;
  }
`;

export const TimelineCard = styled.div`
  background: ${({ theme }) => theme.semantic.background.secondary};
  border: 1px solid ${({ theme }) => theme.semantic.border.subtle};
  border-radius: ${({ theme }) => theme.radii.lg};
  padding: ${({ theme }) => theme.spacing[6]};
  transition:
    border-color 0.3s ease,
    box-shadow 0.3s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.cyan[400]}44;
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

export const TimelineYear = styled.span`
  display: inline-block;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  letter-spacing: ${({ theme }) => theme.letterSpacings.widest};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.cyan[400]};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

export const TimelineTitle = styled.h4`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.semantic.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

export const TimelineText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.semantic.text.tertiary};
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
`;

/* ── Cards MVV ─────────────────────────────────────────────── */

export const MvvGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[5]};
  margin-top: ${({ theme }) => theme.spacing[10]};

  & > div {
    flex: 1 1 260px;
  }
`;

export const MvvCard = styled(motion.div)<{ $accent: string }>`
  position: relative;
  padding: ${({ theme }) => theme.spacing[8]};
  border-radius: ${({ theme }) => theme.radii.xl};
  background: ${({ theme }) => theme.semantic.background.secondary};
  border: 1px solid ${({ theme }) => theme.semantic.border.subtle};
  overflow: hidden;
  transition:
    border-color 0.3s ease,
    transform 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${({ $accent }) => $accent};
  }

  &:hover {
    border-color: rgba(0, 168, 240, 0.25);
    transform: translateY(-4px);
  }
`;

export const MvvIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

export const MvvTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.semantic.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

export const MvvText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.semantic.text.tertiary};
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
`;

/* ── Diferenciais ──────────────────────────────────────────── */

export const DiferenciaisGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[4]};
  margin-top: ${({ theme }) => theme.spacing[10]};

  & > div {
    flex: 1 1 240px;
  }
`;

export const DiferencialCard = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[6]};
  border-radius: ${({ theme }) => theme.radii.lg};
  border: 1px solid ${({ theme }) => theme.semantic.border.subtle};
  background: ${({ theme }) => theme.semantic.background.primary};
  transition:
    border-color 0.3s ease,
    box-shadow 0.3s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.cyan[400]}44;
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

export const DiferencialIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: ${({ theme }) => theme.radii.md};
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.navy[700]},
    ${({ theme }) => theme.colors.steel[500]}
  );
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
`;

export const DiferencialTitle = styled.h4`
  font-size: ${({ theme }) => theme.fontSizes.base};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.semantic.text.primary};
`;

export const DiferencialText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.semantic.text.tertiary};
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
`;

/* ── CTA ───────────────────────────────────────────────────── */

export const CtaSection = styled.section`
  position: relative;
  padding-block: ${({ theme }) => theme.spacing[20]};
  overflow: hidden;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.navy[900]} 0%,
    ${({ theme }) => theme.colors.navy[700]} 100%
  );
`;

export const CtaGlow = styled.div`
  position: absolute;
  bottom: -120px;
  left: -80px;
  width: 500px;
  height: 500px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0, 168, 240, 0.2) 0%, transparent 70%);
  pointer-events: none;
`;

export const CtaContent = styled(Container)`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${({ theme }) => theme.spacing[6]};
`;

export const CtaButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: center;
`;
